<script setup lang="ts">
import type { HelpIconType } from '@design-system/mocks/helpIconTypes'

definePageMeta({
  layout: 'static-page-layout',
  name: 'contact-us',
})

const {
  getContactsPage,
  getAllContacts,
  openLink,
  isContactMethodValid,
  isChatMethod,
} = useContacts()
const { data } = await getContactsPage('contactus-page')
const localPath = useLocalePath()
const { helpShortcutsIcons } = useShortcuts()
const { ts } = useI18n()
const { handleContactClick } = useContactUsSideSlide()
const { insideChat } = useInsideChat()

const { data: allContactMethods } = await getAllContacts('all-contact-methods')

const contactMethods = computed(() => {
  return allContactMethods.value?.content.methods
    .filter(method => isContactMethodValid(method?.name))
    .map(method => {
      return {
        name: method.name,
        description: method.description,
        icon: method.icon,
        cta: {
          label: method.cta?.label || '',
          ariaLabel: method.cta?.ariaLabel,
          internalLink: {
            link: method.cta?.internalCta?.link || '',
            type: method.cta?.internalCta?.type,
          },
          externalLink: {
            link: method.cta?.externalLink?.link || '',
            target: method.cta?.externalLink?.target || 'self',
          },
        },
        hideForAll: !method?.hideInAllCountries?.value?.contactsPage,
        hideInCountry: !method?.hideInCountry?.contactsPage,
      }
    })
})

useHead({
  title: `${ts('customerService.contactUs')} - Armani`,
})
</script>

<template>
  <div class="mx-auto flex flex-col gap-y-10 lg:gap-y-14">
    <div class="gap-y-md flex flex-col">
      <h1 class="text-book-3 md:text-book-2 uppercase">
        {{ $ts('customerService.contactUs') }}
      </h1>

      <div class="bg-neutral-white md:gap-md md:p-md flex flex-col gap-4 p-4">
        <h2 class="text-medium-6 md:text-medium-5">
          {{ data?.content?.navigation?.title }}
        </h2>
        <div class="grid grid-cols-2 gap-4 md:grid-cols-3">
          <AtomsShortcut
            v-for="(shortcut, i) in data?.content?.navigation?.links"
            :key="shortcut._meta.name"
            size="S"
            orientation="horizontal"
            bg-color="offWhite"
            :link="localPath(shortcut.target?.target || '#')"
            :class="{ 'col-span-2 md:col-span-1': i === 0 }"
          >
            <template #icon>
              <component
                :is="helpShortcutsIcons[shortcut.icon as HelpIconType]!.icon"
                :aria-hidden="true"
              />
            </template>
            <template #text>{{ shortcut.target.label }}</template>
          </AtomsShortcut>
        </div>
      </div>
    </div>

    <div class="bg-neutral-white md:gap-md md:p-md flex flex-col gap-4 p-4">
      <h2 class="text-medium-6 md:text-medium-5">
        {{ data?.content?.contactMethods?.title }}
      </h2>
      <UtilsMarkdown
        v-if="data?.content?.contactMethods?.subtitle"
        :content="data?.content?.contactMethods?.subtitle"
        class="text-book-6"
      />
      <div class="gap-sm flex auto-rows-fr grid-cols-2 flex-col md:grid">
        <template v-for="method in contactMethods" :key="method.name">
          <ContactMethod
            v-if="method.hideForAll && method.hideInCountry"
            v-bind="method"
            @click="
              (cta, type) =>
                isChatMethod(method?.name)
                  ? handleContactClick('chat', method.cta)
                  : openLink(cta, type)
            "
          />
        </template>
      </div>
      <UtilsMarkdown
        v-if="data?.content?.contactMethods?.notes"
        :content="data?.content?.contactMethods?.notes"
        class="text-book-8"
      />
    </div>

    <div v-if="data?.content?.other?.length" class="gap-md flex flex-col">
      <p>
        {{ $ts('customerService.contactUsPage.otherSupports.title') }}
      </p>
      <div class="gap-md grid grid-cols-12">
        <template v-for="(card, i) in data?.content?.other" :key="card.title">
          <div
            v-if="!card.hideInCountry"
            class="bg-neutral-white col-span-12 flex flex-col items-center justify-between gap-y-6 px-4 py-8 md:px-16 md:py-16"
            :class="[i === 0 ? 'md:col-span-12' : 'md:col-span-6']"
          >
            <h3 class="text-book-4 text-center">{{ card.title }}</h3>
            <UtilsMarkdown
              v-if="card.description"
              :content="card.description"
              class="text-light-5 text-center"
            />

            <div v-if="card.link" class="gap-md flex flex-col md:flex-row">
              <MoleculesLinkWrapper
                v-for="link in card.link"
                :key="link._meta.name"
                anatomy="link"
                :to="link.target.target"
              >
                <template #label>
                  {{ link.target.label }}
                </template>
              </MoleculesLinkWrapper>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div
      class="text-medium-4 flex flex-col items-center justify-center gap-y-6"
    >
      <p>{{ $ts('customerService.contactUsPage.faq') }}</p>
      <MoleculesLinkWrapper
        anatomy="primary"
        size="s"
        :to="localPath('/help/faq/')"
      >
        <template #label>
          {{ $ts('customerService.contactUsPage.faqBtn') }}
        </template>
      </MoleculesLinkWrapper>
    </div>
  </div>
</template>
